import React from 'react';
import { FetchResult } from '@apollo/client';

import { feedType, filterOps } from '@buyside/components/presentation/CompanyShowPage/entityFeed';
import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import StatusUpdateForm from '@buyside/components/presentation/StatusUpdateForm/statusUpdateForm';

import {
  CreatePostMutationVariables,
  CreatePostMutation,
  FeedDocument,
  FeedQueryVariables,
  FeedQuery,
  Maybe,
  useEntityPostQuery,
  useCreatePostMutation
} from '@interfaces/graphql-typings';

import styles from '@buyside/components/presentation/CompanyShowPage/companyShowPageStyles.scss';

const EntityShowPost: React.FC<{ id: string }> = ({ id }): Maybe<React.ReactElement> => {
  const { data, loading, error } = useEntityPostQuery({ variables: { id } });

  const [createPost, { error: mutationError }] = useCreatePostMutation({
    update(cache, { data }) {
      const newFeedItem = data?.createPost?.feedItem;
      if (newFeedItem) {
        cache.updateQuery<FeedQuery, FeedQueryVariables>(
          {
            query: FeedDocument,
            variables: { feedId: id, feedType, feedFilter: filterOps[0] }
          },
          data => {
            if (!data || !data?.feedItems?.edges || !data.feedItems?.pageInfo) return;
            return {
              feedItems: {
                edges: [{ node: newFeedItem }, ...data.feedItems.edges],
                pageInfo: data.feedItems.pageInfo
              }
            };
          }
        );
      }
    }
  });

  if (loading) return <LoadingSpinner size={SIZES.MEDIUM} />;

  if (error) throw error;

  if (!data?.currentUser || !data?.entity) return null;

  const { tickerTagString } = data.entity;
  const { acceptedPostTerms, canPost, isAdmin, profileImageUrl } = data.currentUser;

  if (!canPost) return null;

  return (
    <>
      <StatusUpdateForm
        isAdmin={isAdmin}
        onSubmit={(values: CreatePostMutationVariables): Promise<FetchResult<CreatePostMutation>> =>
          createPost({ variables: values })
        }
        profileImageUrl={profileImageUrl}
        termsAccepted={acceptedPostTerms}
        tickerTagString={tickerTagString}
      />
      <div className={styles.error}>{mutationError?.graphQLErrors[0]?.message}</div>
    </>
  );
};

export default EntityShowPost;
